import { cn } from '@/lib/utils'

interface KeyValueListProps {
  data: Record<string, unknown>
  title?: string
  excludeKeys?: string[]
  maxItems?: number
  compact?: boolean
}

export function KeyValueList({
  data,
  title,
  excludeKeys = [],
  maxItems,
  compact = false,
}: KeyValueListProps) {
  // Filter out excluded keys and complex values (arrays, objects)
  const entries = Object.entries(data)
    .filter(([key, value]) => {
      if (excludeKeys.includes(key)) return false
      if (key.startsWith('_')) return false // Skip internal hints
      if (Array.isArray(value)) return false
      if (value !== null && typeof value === 'object') return false
      return true
    })
    .slice(0, maxItems)

  if (entries.length === 0) return null

  // Humanize key names
  const humanizeKey = (key: string) =>
    key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase())
      .trim()

  // Format values nicely
  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '-'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (typeof value === 'number') {
      if (value < 0.001 && value > 0) return value.toExponential(3)
      return value.toLocaleString(undefined, { maximumFractionDigits: 4 })
    }
    return String(value)
  }

  return (
    <div className={cn(compact ? 'space-y-1' : 'space-y-2')}>
      {title && (
        <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          {title}
        </h4>
      )}
      <dl
        className={cn(
          'grid gap-x-4',
          compact ? 'gap-y-0.5 text-xs' : 'gap-y-1 text-sm',
          'grid-cols-[auto_1fr]',
        )}
      >
        {entries.map(([key, value]) => (
          <div key={key} className="contents">
            <dt className="text-muted-foreground font-medium truncate">{humanizeKey(key)}</dt>
            <dd className="text-foreground truncate" title={formatValue(value)}>
              {formatValue(value)}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  )
}
